/**
 * WaitingListV2Page — Main page for Waiting List V2.
 *
 * Two tabs:
 * - Pacientes na lista: patient list with inline quick-offer action
 * - Horários vagos: slot table grouped by day with compatible patients
 */
import React, { useState, useCallback, useMemo } from 'react';
import { Tab, Tabs, Alert, Button } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

import AreaTitle from '../../components/areaTitle/AreaTitle';
import CustomButton from '../../components/buttons/CustomButton';
import Loading from '../../components/loading/Loading';
import ConfirmPopup from '../../popup/ConfirmPopup/ConfirmPopup';
import AddWaitingListPopup from '../../popup/AddWaitingListPopup/AddWaitingListPopup';

import WaitingListStats from './components/WaitingListStats';
import WaitingListFilters from './components/WaitingListFilters';
import SlotsTable from './components/SlotsTable';
import PatientListSection from './components/PatientListSection';
import CompatiblePatientsDrawer from './components/CompatiblePatientsDrawer';
import DemoOfferPreviewPopup from './components/DemoOfferPreviewPopup';
import { isDemoRecord } from '../../components/demo-data/demoData';

import { SendWhatsAppMessage } from '../../api/WhatsappAPI';
import MessagesTemplate from '../../consts/MessagesTemplate';
import { buildWaitingListOfferMessage } from '../../utils/waitingListMessageUtils';
import { buildOpportunitiesFromPatients } from './utils/opportunityMapper';
import { ATTENDANCE_ONLINE, normalizeAttendanceMode } from '../appointments/v2/utils/attendanceMode';
import { useWaitingListData } from './hooks/useWaitingListData';
import usePlanLock from '../../hooks/usePlanLock';
import { SUBSCRIPTION_PERMISSIONS } from '../../consts/SubscriptionFeatures';
import styles from './WaitingListV2Page.module.css';

const TABS = { PATIENTS: 'PATIENTS', SLOTS: 'SLOTS' };

/** "2026-10-18" → "18/10" */
function formatShortDate(dateStr) {
  const [, month, day] = (dateStr || '').split('-');
  return month && day ? `${day}/${month}` : '';
}

function getDefaultDateRange() {
  // Data local (toISOString é UTC e virava o dia seguinte depois das 21h)
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const today = new Date();
  const end = new Date();
  end.setDate(end.getDate() + 7);
  return { startDate: ymd(today), endDate: ymd(end) };
}

function WaitingListV2Page() {
  const defaultDates = useMemo(() => getDefaultDateRange(), []);
  // Profissional filtra no servidor (useWaitingListData); os demais filtros, aqui
  const [filters, setFilters] = useState({
    status: '',
    resourceId: '',
    procedureId: '',
    sortBy: 'compatibility',
    ...defaultDates,
  });

  const {
    patients,
    loading,
    error,
    loadData,
    updatePatient,
    deletePatient,
    registerOffer,
    lastOffers,
    createAppointment,
    companyConfig,
    userData,
    getPatientById,
    getProcedureById,
    getResourceById,
  } = useWaitingListData({ resourceId: filters.resourceId });

  // UI state
  const [activeTab, setActiveTab] = useState(TABS.PATIENTS);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [alertError, setAlertError] = useState(null);
  // Prévia da oferta de um item de exemplo: { name, message }
  const [demoOfferPreview, setDemoOfferPreview] = useState(null);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [drawerSortBy, setDrawerSortBy] = useState('compatibility');

  // Offer state: tracks which slot was offered per waiting list item
  // key: "waitingListItemId_slotDate_slotTime", value: 'offered' | 'accepted' | 'denied'
  const [offeredSlots, setOfferedSlots] = useState({});

  // Build opportunities from patient data
  const opportunities = useMemo(() => {
    if (!patients || patients.length === 0) return [];
    return buildOpportunitiesFromPatients(patients, {
      getPatientById,
      getProcedureById,
      getResourceById,
      dailyGoal: companyConfig?.DailyGoal || 8,
    });
  }, [patients, getPatientById, getProcedureById, getResourceById, companyConfig?.DailyGoal]);

  // Transform opportunities into flat slot list for SlotsTable
  const slotsForTable = useMemo(() => {
    const slots = [];
    opportunities.forEach((opp) => {
      (opp.slots || [opp]).forEach((slot) => {
        const resource = slot.resourceId ? getResourceById(slot.resourceId) : null;
        const procedure = slot.procedureId ? getProcedureById(slot.procedureId) : null;
        slots.push({
          date: slot.date || opp.date,
          time: slot.time || opp.time,
          duration: slot.duration || opp.duration || 30,
          resourceId: slot.resourceId || opp.resourceId,
          resourceName: resource?.Label || slot.resourceName || opp.resourceName || '',
          resourceSpecialty: resource?.Specialty || '',
          procedureId: slot.procedureId || opp.procedureId,
          procedureName: procedure?.Label || slot.procedureName || opp.procedureName || '',
          roomName: slot.roomName || opp.roomName || '',
          compatibleCount: slot.patients?.length || opp.patients?.length || 0,
          patients: slot.patients || opp.patients || [],
        });
      });
    });
    return slots.filter((s) => {
      // Horário de entrada "Qualquer profissional" (sem resourceId) também serve ao filtrado
      if (filters.resourceId && s.resourceId && s.resourceId != filters.resourceId) return false;
      if (filters.procedureId && s.procedureId != filters.procedureId) return false;
      if (filters.startDate && s.date < filters.startDate) return false;
      if (filters.endDate && s.date > filters.endDate) return false;
      return true;
    });
  }, [opportunities, filters, getResourceById, getProcedureById]);

  // Build suggestionsMap and patientSlotsMap from opportunities
  const { suggestionsMap, patientSlotsMap } = useMemo(() => {
    const sMap = {};
    const pSlotsMap = {};
    opportunities.forEach((opp) => {
      (opp.patients || []).forEach((p) => {
        const wlItem = p.waitingListItem;
        if (wlItem?.id) {
          sMap[wlItem.id] = (sMap[wlItem.id] || 0) + 1;
          if (!pSlotsMap[wlItem.id]) pSlotsMap[wlItem.id] = [];
          pSlotsMap[wlItem.id].push({
            date: opp.date,
            time: opp.time,
            datetime: opp.datetime,
            resourceId: opp.resourceId,
            resourceLabel: opp.resourceLabel,
            score: p.score,
          });
        }
      });
    });
    return { suggestionsMap: sMap, patientSlotsMap: pSlotsMap };
  }, [opportunities]);

  // Build bestSlotMap: waitingListItemId → best slot (highest score)
  const bestSlotMap = useMemo(() => {
    const map = {};
    Object.entries(patientSlotsMap).forEach(([itemId, slots]) => {
      if (slots.length === 0) return;
      const best = slots.reduce((a, b) => ((b.score || 0) > (a.score || 0) ? b : a), slots[0]);
      map[itemId] = {
        slotDate: best.date,
        slotTime: best.time,
        slotDatetime: best.datetime,
        resourceId: best.resourceId,
        resourceLabel: best.resourceLabel,
        compatibilityScore: best.score || 80,
      };
    });
    return map;
  }, [patientSlotsMap]);

  // Filter patients for the patients tab
  const filteredPatients = useMemo(() => {
    return patients.filter((p) => {
      if (filters.status && p.status !== filters.status) return false;
      if (filters.resourceId && p.resourceId && p.resourceId != filters.resourceId) return false;
      if (filters.procedureId && p.procedureId != filters.procedureId) return false;
      return true;
    });
  }, [patients, filters]);

  // Build compatible patients for slot drawer
  const compatiblePatientsForDrawer = useMemo(() => {
    if (!selectedSlot) return [];

    // Patient view mode: each item represents an available slot
    if (selectedSlot._isPatientView) {
      return (selectedSlot._suggestedSlots || []).map((s, idx) => ({
        id: `slot-${idx}`,
        patientId: selectedSlot._patientId,
        waitingListItemId: selectedSlot._waitingListItemId,
        patientName: selectedSlot._patientName,
        patientPhone: selectedSlot._patientPhone,
        priority: selectedSlot._patientPriority,
        waitingDays: selectedSlot._patientWaitingDays || 0,
        slotDate: s.date,
        slotTime: s.time,
        slotDatetime: s.datetime,
        resourceId: s.resourceId,
        resourceLabel: s.resourceLabel,
        compatibilityScore: s.score || 80,
      })).sort((a, b) => b.compatibilityScore - a.compatibilityScore);
    }

    // Default mode: each item represents a compatible patient
    return (selectedSlot.patients || []).map((p, idx) => {
      const patient = getPatientById(p.patientId + '');
      const wlItem = p.waitingListItem;
      let preferredTimesLabel = '—';
      try {
        const raw = wlItem?.preferredTimes;
        const times = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (Array.isArray(times) && times.length > 0) {
          const labels = [];
          if (times.some(t => parseInt(t) < 12)) labels.push('Manhã');
          if (times.some(t => parseInt(t) >= 12)) labels.push('Tarde');
          preferredTimesLabel = labels.join(', ');
        }
      } catch (e) { /* ignore */ }

      return {
        id: wlItem?.id || p.patientId,
        patientId: p.patientId,
        name: p._realPatientName || patient?.name || p.patientName || 'Paciente',
        phone: p.patientPhone || patient?.phone || '',
        patientCountry: p.patientCountry || patient?.countryCode || '',
        priority: p.priority,
        waitingDays: p.waitingDays || 0,
        preferredTimesLabel,
        lastContact: p.status === 'CONTACTED' ? 'Via WhatsApp' : '—',
        compatibilityScore: p.score || Math.max(60, Math.min(99, 95 - idx * 6)),
      };
    }).sort((a, b) => {
      if (drawerSortBy === 'waitingTime') return b.waitingDays - a.waitingDays;
      if (drawerSortBy === 'priority') {
        const order = { HIGH: 3, MEDIUM: 2, LOW: 1 };
        return (order[b.priority] || 0) - (order[a.priority] || 0);
      }
      return b.compatibilityScore - a.compatibilityScore;
    });
  }, [selectedSlot, getPatientById, drawerSortBy]);

  // Derive the offered slot key for the drawer's best opportunity card
  const drawerOfferedSlotKey = useMemo(() => {
    if (!selectedSlot?._isPatientView || !selectedSlot._waitingListItemId) return null;
    const itemId = selectedSlot._waitingListItemId;
    const entry = Object.entries(offeredSlots).find(([k]) => k.startsWith(`${itemId}_`));
    if (!entry) return null;
    // key format: "itemId_slotDate_slotTime" → return "slotDate_slotTime"
    const parts = entry[0].split('_');
    return parts.slice(1).join('_');
  }, [selectedSlot, offeredSlots]);

  // ── Handlers ──────────────────────────────────────────

  const handleViewPatients = useCallback((slot) => {
    setSelectedSlot(slot);
    setDrawerOpen(true);
  }, []);

  const handleViewSuggestions = useCallback((item) => {
    const patientSlots = patientSlotsMap[item.id] || [];
    const patient = getPatientById(item.patientId + '');
    const procedure = getProcedureById(item.procedureId);
    const resource = item.resourceId ? getResourceById(item.resourceId) : null;

    setSelectedSlot({
      _isPatientView: true,
      _patientId: item.patientId,
      _waitingListItemId: item.id,
      _patientName: patient?.name || 'Paciente',
      _patientPhone: patient?.phone || '',
      _patientPriority: item.priority,
      _patientWaitingDays: item.waitingDays || 0,
      procedureName: procedure?.Label || '',
      _suggestedSlots: patientSlots.map((s) => ({
        date: s.date,
        time: s.time,
        datetime: s.datetime,
        resourceId: s.resourceId,
        resourceLabel: s.resourceLabel || resource?.Label || '',
        score: s.score || 80,
      })),
    });
    setDrawerOpen(true);
  }, [patientSlotsMap, getPatientById, getProcedureById, getResourceById]);

  // Cadeado por plano: a tela continua visível; criar/editar abre o modal de upgrade.
  const lock = usePlanLock(SUBSCRIPTION_PERMISSIONS.WAITING_LIST, 'A lista de espera');
  const { locked: planLocked, openUpgrade: openPlanUpgrade } = lock;

  const handleEdit = useCallback((item) => {
    if (planLocked) { openPlanUpgrade(); return; }
    setEditItem(item);
    setShowAddPopup(true);
  }, [planLocked, openPlanUpgrade]);

  const handleDeleteRequest = useCallback((item) => {
    setSelectedItem(item);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!selectedItem) return;
    deletePatient(selectedItem.id, (response) => {
      if (!response.success) setAlertError('Erro ao remover paciente: ' + response.error);
    });
    setShowDeleteConfirm(false);
    setSelectedItem(null);
  }, [selectedItem, deletePatient]);

  const handleAddSaved = useCallback(() => {
    loadData();
    setShowAddPopup(false);
    setEditItem(null);
  }, [loadData]);

  // ── Core offer logic (shared by inline offer and drawer offer) ──────────
  const sendOffer = useCallback((patientData, slotDate, slotTime, slotResourceId) => {
    const realPatient = getPatientById(patientData.patientId + '');
    const phone = realPatient?.phone || patientData.patientPhone || patientData.phone || '';
    const realName = realPatient?.name || patientData.patientName || patientData.name || '';
    const resource = slotResourceId ? getResourceById(slotResourceId) : null;

    const rawMessage = buildWaitingListOfferMessage({
      patientName: realName,
      slotDate,
      slotTime,
      resourceLabel: resource?.Label || '',
      companyConfig,
    });

    // Item de exemplo (trial-demo-data): só mostra a mensagem que sairia. Nada é
    // enviado, nenhuma oferta é registrada e o status não muda.
    const waitingListItemId = patientData.waitingListItemId || patientData.id;
    const waitingItem = patients.find((p) => String(p.id) === String(waitingListItemId));
    if (isDemoRecord(waitingItem)) {
      setDemoOfferPreview({ name: realName, message: rawMessage });
      return false;
    }

    if (!phone) {
      setAlertError('Paciente não possui telefone cadastrado.');
      return false;
    }

    const countryCode = realPatient?.countryCode || '';
    SendWhatsAppMessage(phone, MessagesTemplate.ConvertToWhatsAppText(rawMessage), countryCode);

    // Registro da oferta (set_offer.php: waitingListId + offeredDate): quem e quando
    registerOffer({
      waitingListId: waitingListItemId,
      patientId: patientData.patientId,
      offeredDate: `${slotDate} ${slotTime || '00:00'}:00`,
      channel: 'whatsapp',
    }, () => {});

    updatePatient({ id: patientData.waitingListItemId || patientData.id, status: 'CONTACTED' }, () => {});

    return true;
  }, [companyConfig, registerOffer, updatePatient, getPatientById, getResourceById, patients]);

  // ── Oferecer horário via WhatsApp (from drawer) ──────────────────────────
  const handleOfferSlot = useCallback((patient, slot) => {
    const slotDate = patient.slotDate || slot?.date || '';
    const slotTime = patient.slotTime || slot?.time || '';
    const ok = sendOffer(patient, slotDate, slotTime, patient.resourceId || slot?.resourceId);
    if (ok) {
      const key = `${patient.waitingListItemId || patient.id}_${slotDate}_${slotTime}`;
      setOfferedSlots(prev => ({ ...prev, [key]: 'offered' }));
    }
  }, [sendOffer]);

  // ── Inline quick-offer from patient row ──────────────────────────────────
  const handleOfferBestSlot = useCallback((item, bestSlot) => {
    const patientData = {
      patientId: item.patientId,
      waitingListItemId: item.id,
      patientPhone: getPatientById(item.patientId + '')?.phone || '',
      name: getPatientById(item.patientId + '')?.name || '',
    };
    const ok = sendOffer(patientData, bestSlot.slotDate, bestSlot.slotTime, bestSlot.resourceId);
    if (ok) {
      const key = `${item.id}_${bestSlot.slotDate}_${bestSlot.slotTime}`;
      setOfferedSlots(prev => ({ ...prev, [key]: 'offered' }));
    }
  }, [sendOffer, getPatientById]);

  // ── Agendar diretamente ───────────────────────────────────────────────────
  const handleSchedule = useCallback((patient, slot) => {
    const slotDate = patient.slotDate || slot?.date || '';
    const slotTime = patient.slotTime || slot?.time || '';

    if (!slotDate || !patient?.patientId) {
      setAlertError('Dados insuficientes para criar agendamento.');
      return;
    }

    const datetime = slotTime ? `${slotDate} ${slotTime}:00` : `${slotDate} 08:00:00`;
    // Chega a entrada da lista ou uma linha de sugestão: o procedimento e a agenda vêm da entrada
    const waitingListItemId = patient.waitingListItemId || patient.id;
    const item = patients.find(p => String(p.id) === String(waitingListItemId)) || patient;
    const procedure = getProcedureById(item.procedureId);
    // Modalidade e link pedidos na entrada (entradas antigas = presencial)
    const attendanceMode = normalizeAttendanceMode(item.attendance_mode);
    const isOnline = attendanceMode === ATTENDANCE_ONLINE;

    // Mesmo formato do agendamento do calendário (SetAppointment); agenda "Qualquer"
    // vai vazia e o backend escolhe uma agenda livre no horário
    createAppointment({
      patient: item.patientId || patient.patientId,
      resource: item.resourceId || patient.resourceId || slot?.resourceId || null,
      procedure: item.procedureId || null,
      date: datetime,
      status: 'scheduled',
      duration: procedure?.Duration ?? null,
      price: procedure?.Price ?? null,
      attendance_mode: attendanceMode,
      meeting_url: isOnline ? (item.meeting_url || null) : null,
      waitingListItemId,
    }, (response) => {
      if (response?.status === 'SUCCESS') {
        setDrawerOpen(false);
        loadData();
      } else {
        setAlertError(response?.message || 'Erro ao criar agendamento. Tente novamente.');
      }
    });
  }, [createAppointment, loadData, patients, getProcedureById]);

  // ── Offer response handlers ───────────────────────────────────────────────
  const handleOfferAccepted = useCallback((patient, slot) => {
    handleSchedule(patient, slot);
  }, [handleSchedule]);

  const handleOfferDenied = useCallback((patient) => {
    const key = `${patient.waitingListItemId || patient.id}_${patient.slotDate}_${patient.slotTime}`;
    setOfferedSlots(prev => ({ ...prev, [key]: 'denied' }));
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  // Tela cheia de carregamento só na primeira carga; ao trocar o filtro a lista atual fica
  if (loading && !showAddPopup && patients.length === 0) {
    return (
      <div className="page-content">
        <Loading />
      </div>
    );
  }

  const selectedPatient = selectedItem ? getPatientById(selectedItem.patientId + '') : null;
  const opportunityCount = slotsForTable.length;
  // Mesmo período no card "Com encaixe possível" e na faixa abaixo dos filtros
  const periodLabel = filters.startDate && filters.endDate
    ? `de ${formatShortDate(filters.startDate)} a ${formatShortDate(filters.endDate)}`
    : '';

  return (
    <div className="page-content">
      {(error || alertError) && (
        <Alert severity="error" onClose={() => setAlertError(null)} style={{ marginBottom: 12 }}>
          {error || alertError}
        </Alert>
      )}

      {lock.banner && <div style={{ marginBottom: 12 }}>{lock.banner}</div>}

      {/* Header */}
      <div className={styles.header}>
        <div>
          <AreaTitle Title="Lista de Espera" />
          <div className={styles.headerSubtitle}>
            Encontre pacientes para preencher horários vagos
          </div>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="text"
            startIcon={<RefreshIcon />}
            onClick={() => loadData()}
            sx={{ textTransform: 'none', color: '#64748b', fontSize: '0.82rem' }}
          >
            Atualizar
          </Button>
          <CustomButton
            variant="contained"
            style="secondary"
            label="Novo paciente"
            startIcon={<AddIcon />}
            onClick={lock.guard(() => { setEditItem(null); setShowAddPopup(true); })}
          />
        </div>
      </div>

      {/* Stats */}
      <WaitingListStats patients={patients} slots={slotsForTable} periodLabel={periodLabel} />

      {/* Tabs */}
      <div className={styles.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontSize: '0.88rem', fontWeight: 500 },
            '& .Mui-selected': { color: '#e11d48' },
            '& .MuiTabs-indicator': { backgroundColor: '#e11d48' },
          }}
        >
          <Tab
            value={TABS.PATIENTS}
            label="Pacientes na lista"
            icon={<PeopleOutlineIcon sx={{ fontSize: '1rem' }} />}
            iconPosition="start"
          />
          <Tab
            value={TABS.SLOTS}
            label="Horários vagos"
            icon={<CalendarTodayIcon sx={{ fontSize: '1rem' }} />}
            iconPosition="start"
          />
        </Tabs>
      </div>

      <div className={styles.tabContent}>
        {/* Filters */}
        <WaitingListFilters
          filters={filters}
          onFilterChange={setFilters}
          resources={companyConfig?.Resources || []}
          procedures={companyConfig?.Procedures || []}
          activeTab={activeTab}
        />

        {/* Opportunities banner */}
        {activeTab === TABS.PATIENTS && opportunityCount > 0 && (
          <div className={styles.opportunitiesBanner} onClick={() => setActiveTab(TABS.SLOTS)}>
            <EventAvailableIcon sx={{ fontSize: '1.2rem', color: '#059669' }} />
            <div>
              <strong>
                {opportunityCount} horário{opportunityCount !== 1 ? 's' : ''} livre{opportunityCount !== 1 ? 's' : ''} para encaixar quem está na lista
              </strong>
              <div style={{ fontSize: '0.78rem', marginTop: 2, opacity: 0.85 }}>
                {periodLabel
                  ? `${periodLabel.charAt(0).toUpperCase()}${periodLabel.slice(1)}. Veja quem pode ocupar cada um.`
                  : 'Veja quem pode ocupar cada um.'}
              </div>
            </div>
            <span style={{ marginLeft: 'auto', textDecoration: 'underline', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
              Ver horários →
            </span>
          </div>
        )}

        {/* Tab content */}
        {activeTab === TABS.PATIENTS && (
          <PatientListSection
            patients={filteredPatients}
            suggestionsMap={suggestionsMap}
            bestSlotMap={bestSlotMap}
            getPatientById={getPatientById}
            getProcedureById={getProcedureById}
            getResourceById={getResourceById}
            onViewSuggestions={handleViewSuggestions}
            onOfferBestSlot={handleOfferBestSlot}
            onDelete={handleDeleteRequest}
            onEdit={handleEdit}
            lastOffers={lastOffers}
          />
        )}
        {activeTab === TABS.SLOTS && (
          <SlotsTable
            slots={slotsForTable}
            onViewPatients={handleViewPatients}
          />
        )}
      </div>

      {/* Drawer */}
      <CompatiblePatientsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slot={selectedSlot}
        compatiblePatients={compatiblePatientsForDrawer}
        sortBy={drawerSortBy}
        onSortChange={setDrawerSortBy}
        onOfferSlot={handleOfferSlot}
        onSchedule={handleSchedule}
        offeredSlotKey={drawerOfferedSlotKey}
        onOfferAccepted={handleOfferAccepted}
        onOfferDenied={handleOfferDenied}
        onOfferResend={handleOfferSlot}
      />

      {lock.modal}

      <DemoOfferPreviewPopup
        preview={demoOfferPreview}
        onClose={() => setDemoOfferPreview(null)}
      />

      {/* Add/Edit Popup */}
      {showAddPopup && (
        <AddWaitingListPopup
          OnHide={() => { setShowAddPopup(false); setEditItem(null); }}
          OnSaved={handleAddSaved}
          initialData={editItem}
        />
      )}

      {/* Delete Confirm */}
      {showDeleteConfirm && selectedItem && (
        <ConfirmPopup
          Visibility={showDeleteConfirm}
          Title="Remover paciente"
          Description={`Remover ${selectedPatient?.name || 'paciente'} da lista de espera?`}
          OnHide={() => { setShowDeleteConfirm(false); setSelectedItem(null); }}
          OnConfirm={handleDeleteConfirm}
          OnCancel={() => { setShowDeleteConfirm(false); setSelectedItem(null); }}
        />
      )}
    </div>
  );
}

export default WaitingListV2Page;
